import { InfoTip } from "@/components/ui/info-tip";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowLeft, Download, Mail } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import { ApiError } from "@/lib/api";
import { Permission, useCan } from "@/components/session-provider";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { ChartLegend } from "@/components/analytics/quality-charts";
import {
  NOTIFICATION_REPORT_DAYS,
  getNotificationReport,
  notificationReportExportUrl,
  type NotificationReport,
} from "@/lib/reports-notifications";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/reports/notifications")({
  component: NotificationActivityPage,
});

/**
 * **Notification activity.**
 *
 * ⚠️ **WHY THIS EXISTS.** A two-week email-delivery incident (a too-short retry policy, a
 * batch-wide save rolling back on one bad row, serial sends starving a batch, a self-inflicted
 * `DbContext` race, and finally an ACS domain send quota) was diagnosed entirely from ad hoc SQL
 * and Application Insights traces pulled live during the fire. Every one of those would have been
 * visible on a chart of Pending/Sent/Failed volume over time, checked before anybody had to.
 *
 * ⚠️ **Gated on `settings.read`, not on a reporting tier.** The payload is about the outbox's own
 * operational health — counts, outcomes and timing — never about a ticket, a requester or a
 * recipient, so it takes the permission that already covers "state what was switched on" (D-80's
 * pattern applied a fifth time).
 *
 * ⚠️ **No drill-through.** A row here names no ticket and no person, so there is nothing to open.
 */
function NotificationActivityPage() {
  const allowed = useCan(Permission.SettingsRead);

  const [days, setDays] = useState<number>(7);
  const [report, setReport] = useState<NotificationReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!allowed) return;

    let current = true;
    setLoading(true);
    setError(null);

    getNotificationReport(days)
      .then((r) => {
        if (current) setReport(r);
      })
      .catch((e) => {
        // ⚠️ A failure renders as a failure, never as an empty report (D-122). "Nothing has
        // been sent" and "this did not load" are different facts.
        if (current) {
          setReport(null);
          setError(e instanceof ApiError ? e.message : "That report could not be loaded.");
        }
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
  }, [allowed, days]);

  if (!allowed) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <p className="text-sm text-muted-foreground">This report is not available to you.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Reports
      </Link>

      <header className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Mail className="h-5 w-5" /> Notification Activity
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            How many notifications went out, what happened to them, and when.
          </p>
        </div>

        <a
          href={notificationReportExportUrl(days)}
          className="inline-flex h-10 items-center gap-2 rounded-lg border border-border px-4 text-sm font-medium transition hover:bg-muted"
        >
          <Download className="h-4 w-4" /> Export
        </a>
      </header>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label className="text-xs text-muted-foreground" htmlFor="period">
          Period
        </label>
        <select
          id="period"
          className="h-9 rounded-md border border-input bg-background px-2 text-base md:text-sm"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {NOTIFICATION_REPORT_DAYS.map((d) => (
            <option key={d} value={d}>
              Last {d} {d === 1 ? "day" : "days"}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && !report && <p className="mt-6 text-sm text-muted-foreground">Loading…</p>}

      {report && (
        <>
          <p className="mt-4 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            Covering the last {report.coverage.days} {report.coverage.days === 1 ? "day" : "days"},
            counted in UTC.
          </p>

          <section className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Tile tone="neutral" heading="Total" figure={report.totals.total} />
            <Tile tone="good" heading="Sent" figure={report.totals.sent} />
            <Tile tone="bad" heading="Failed" figure={report.totals.failed} />
            <Tile tone="muted" heading="Suppressed" figure={report.totals.suppressed} />
            <Tile tone="muted" heading="Pending" figure={report.totals.pending} />
          </section>

          <Panel
            help="notif-volume"
            title="Volume over time"
            note="Days are UTC, not your own timezone."
          >
            {report.days.length === 0 ? (
              <Empty>Nothing has been queued in this period.</Empty>
            ) : (
              <NotificationVolumeChart days={report.days} />
            )}
          </Panel>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Panel
              help="notif-event"
              title="By event type"
              note="What kind of change triggered the notification."
            >
              {report.byEventType.length === 0 ? (
                <Empty>Nothing has been sent in this period.</Empty>
              ) : (
                <NotificationBreakdownChart
                  rows={report.byEventType.map((e) => ({ label: e.eventType, total: e.total }))}
                />
              )}
            </Panel>

            <Panel help="notif-channel" title="By channel" note="Email against the in-app bell.">
              {report.byChannel.length === 0 ? (
                <Empty>Nothing has been sent in this period.</Empty>
              ) : (
                <NotificationBreakdownChart
                  rows={report.byChannel.map((c) => ({ label: c.channel, total: c.total }))}
                />
              )}
            </Panel>
          </div>

          <Panel
            help="notif-failing"
            title="Why sends are failing"
            note="Already sanitised at the point it was recorded, ranked by how often it recurs."
          >
            {report.failureReasons.length === 0 ? (
              <Empty>Nothing has failed in this period.</Empty>
            ) : (
              <NotificationBreakdownChart
                rows={report.failureReasons.map((f) => ({
                  label: f.reason ?? "(no reason recorded)",
                  total: f.count,
                }))}
              />
            )}
          </Panel>

          <Panel
            help="notif-recent"
            title="Recent"
            note={`The newest ${report.recent.length.toLocaleString()} messages, newest first.`}
          >
            {report.recent.length === 0 ? (
              <Empty>Nothing has been queued in this period.</Empty>
            ) : (
              <div className="max-h-[28rem] overflow-auto">
                <table className="w-full min-w-[52rem] text-left text-sm">
                  <thead className="sticky top-0 bg-card text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-3 font-medium">Created (UTC)</th>
                      <th className="py-2 pr-3 font-medium">Sent (UTC)</th>
                      <th className="py-2 pr-3 font-medium">Event</th>
                      <th className="py-2 pr-3 font-medium">Channel</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 pr-3 font-medium">Attempts</th>
                      <th className="py-2 pr-3 font-medium">Last error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.recent.map((row, i) => (
                      <tr key={i} className="border-t border-border align-top">
                        <td className="py-2 pr-3 whitespace-nowrap tabular-nums">
                          {formatUtcTimestamp(row.createdAtUtc)}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap tabular-nums text-muted-foreground">
                          {row.sentAtUtc ? formatUtcTimestamp(row.sentAtUtc) : "—"}
                        </td>
                        <td className="py-2 pr-3">{row.eventType}</td>
                        <td className="py-2 pr-3">{row.channel}</td>
                        <td className="py-2 pr-3">
                          <StatusBadge status={row.status} />
                        </td>
                        <td className="py-2 pr-3 tabular-nums">{row.attempts}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{row.lastError ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Charts.
//
// ⚠️ Built on the shared chart system — the same `--chart-*` tokens, the same `ChartContainer`,
// the same `ChartLegend` that `report-charts.tsx` uses — rather than a second one. Two chart
// languages in one product make two screens about the same desk look like two products.
// ---------------------------------------------------------------------------

const volumeConfig = {
  sent: { label: "Sent", color: "var(--chart-4)" },
  failed: { label: "Failed", color: "var(--chart-2)" },
  suppressed: { label: "Suppressed", color: "var(--chart-3)" },
  pending: { label: "Pending", color: "var(--chart-1)" },
} satisfies ChartConfig;

function NotificationVolumeChart({ days }: { days: NotificationReport["days"] }) {
  const data = useMemo(
    () => days.map((d) => ({ ...d, label: formatUtcDayLabel(d.dayUtc) })),
    [days],
  );

  const tickInterval = Math.max(0, Math.ceil(data.length / 10) - 1);

  return (
    <>
      <ChartContainer config={volumeConfig} className="aspect-auto h-64 w-full">
        <AreaChart data={data} margin={{ left: 4, right: 8, top: 8, bottom: 0 }}>
          <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" vertical={false} />

          <XAxis
            dataKey="label"
            interval={tickInterval}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            fontSize={11}
            stroke="var(--color-muted-foreground)"
          />
          <YAxis
            allowDecimals={false}
            width={34}
            tickLine={false}
            axisLine={false}
            fontSize={11}
            stroke="var(--color-muted-foreground)"
          />

          <ChartTooltip
            cursor={{ stroke: "var(--color-border)" }}
            content={<ChartTooltipContent indicator="dot" />}
          />

          <Area
            type="monotone"
            dataKey="sent"
            name="Sent"
            stackId="volume"
            stroke="var(--color-sent)"
            fill="var(--color-sent)"
            fillOpacity={0.5}
            strokeWidth={1}
          />
          <Area
            type="monotone"
            dataKey="failed"
            name="Failed"
            stackId="volume"
            stroke="var(--color-failed)"
            fill="var(--color-failed)"
            fillOpacity={0.5}
            strokeWidth={1}
          />
          <Area
            type="monotone"
            dataKey="suppressed"
            name="Suppressed"
            stackId="volume"
            stroke="var(--color-suppressed)"
            fill="var(--color-suppressed)"
            fillOpacity={0.5}
            strokeWidth={1}
          />
          <Area
            type="monotone"
            dataKey="pending"
            name="Pending"
            stackId="volume"
            stroke="var(--color-pending)"
            fill="var(--color-pending)"
            fillOpacity={0.5}
            strokeWidth={1}
          />
        </AreaChart>
      </ChartContainer>

      <ChartLegend
        items={[
          { color: "var(--chart-4)", label: "Sent" },
          { color: "var(--chart-2)", label: "Failed" },
          { color: "var(--chart-3)", label: "Suppressed" },
          { color: "var(--chart-1)", label: "Pending" },
        ]}
        note="Stacked, so the top of the band is the total queued that day."
      />
    </>
  );
}

const breakdownConfig = {
  total: { label: "Notifications", color: "var(--chart-1)" },
} satisfies ChartConfig;

/**
 * One breakdown as ranked horizontal bars — the same shape as `ReportBreakdownChart`
 * (`report-charts.tsx`), rebuilt locally because that component's props are typed to the ticket
 * report's own row shape.
 *
 * ⚠️ ONE HUE, not one per row: this is a MAGNITUDE chart, not an identity one. Colouring event
 * types or channels by hue would imply the colours mean something, and colour by rank repaints
 * every survivor the moment a filter changes the row count.
 */
function NotificationBreakdownChart({ rows }: { rows: { label: string; total: number }[] }) {
  const data = useMemo(() => [...rows].sort((a, b) => b.total - a.total).slice(0, 20), [rows]);

  if (data.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">Nothing in this period.</p>
    );
  }

  const labelWidth = Math.min(160, Math.max(80, ...data.map((row) => row.label.length * 7)));
  const maxLabel = Math.max(12, Math.floor(labelWidth / 7));
  const total = data.reduce((sum, row) => sum + row.total, 0);

  return (
    <>
      <ChartContainer
        config={breakdownConfig}
        className="aspect-auto w-full"
        style={{ height: `${Math.max(120, data.length * 34 + 24)}px` }}
      >
        <BarChart data={data} layout="vertical" margin={{ left: 4, right: 24, top: 4, bottom: 4 }}>
          <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" horizontal={false} />

          <XAxis
            type="number"
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            fontSize={11}
            stroke="var(--color-muted-foreground)"
          />
          <YAxis
            type="category"
            dataKey="label"
            width={labelWidth}
            tickLine={false}
            axisLine={false}
            fontSize={11}
            stroke="var(--color-muted-foreground)"
            tickFormatter={(label: string) =>
              label.length > maxLabel ? `${label.slice(0, maxLabel - 1)}…` : label
            }
          />

          <ChartTooltip
            cursor={{ fill: "var(--color-muted)", fillOpacity: 0.4 }}
            content={<ChartTooltipContent indicator="dot" />}
          />

          <Bar dataKey="total" name="Notifications" radius={[0, 4, 4, 0]} maxBarSize={18}>
            {data.map((row) => (
              <Cell key={row.label} fill="var(--color-total)" />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>

      <p className="mt-2 text-xs text-muted-foreground">{total.toLocaleString()} in total</p>
    </>
  );
}

// ---------------------------------------------------------------------------
// Small pieces.
// ---------------------------------------------------------------------------

function Tile({
  tone,
  heading,
  figure,
}: {
  tone: "neutral" | "good" | "bad" | "muted";
  heading: string;
  figure: number;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border p-4",
        tone === "good" && "border-emerald-500/30 bg-emerald-500/5",
        tone === "bad" && "border-destructive/30 bg-destructive/5",
        tone === "muted" && "border-border bg-muted/40",
        tone === "neutral" && "border-border bg-card",
      )}
    >
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{heading}</p>
      <p
        className={cn(
          "mt-1 text-2xl font-semibold tabular-nums",
          tone === "good" && "text-emerald-700 dark:text-emerald-300",
          tone === "bad" && "text-destructive",
        )}
      >
        {figure.toLocaleString()}
      </p>
    </div>
  );
}

function Panel({
  title,
  note,
  help,
  children,
}: {
  /** Key into the help registry (`lib/help.ts`). Draws the ⓘ beside the title. */
  help?: string;
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="group/card mt-4 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-1">
        <h2 className="text-base font-semibold">{title}</h2>
        {help && <InfoTip id={help} />}
      </div>
      {note && <p className="mt-0.5 text-xs text-muted-foreground">{note}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === "Sent"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
      : status === "Failed"
        ? "bg-destructive/15 text-destructive"
        : "bg-muted text-muted-foreground";

  return (
    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", tone)}>{status}</span>
  );
}

/**
 * ⚠️ Formats a UTC calendar day using UTC getters, never `toLocaleDateString`. The server sends
 * a midnight-UTC instant for a day it deliberately did NOT shift into the reader's timezone
 * (D-135, D-145) — reading it back through the browser's local zone would draw the wrong date
 * west of UTC.
 */
function formatUtcDayLabel(dayUtc: string): string {
  const date = new Date(dayUtc);
  const month = date.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  return `${month} ${date.getUTCDate()}`;
}

/** Same reasoning as {@link formatUtcDayLabel}: UTC in, UTC out. */
function formatUtcTimestamp(value: string): string {
  const date = new Date(value);
  const datePart = formatUtcDayLabel(value);
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
  return `${datePart} ${timePart}`;
}
